import { type AxiosError } from 'axios';
import TripPackageLoader, {
    type TripPackageLoaderResult,
} from '../TripPackageLoader';
import TrainPackage from './TrainPackage';
import { type TrainPackageLoaderConfig } from './types';
import TrainTourPackage from '@itinerary/trip/train/TrainTourPackage';
import { type SellTypeCode } from '@utils/train';
import {
    ServerDisconnectToDateException,
    TrainNoServerResponseError,
} from '@itinerary/Exceptions';
import { type TripType } from '@utils/trip';
import {
    GetAvailableStatus,
    TrainApiApiGetAvailableV2Get,
    type TrainApiApiGetAvailableV2GetRequest,
} from '@clients/TrainApi';

export class TrainPackageLoader extends TripPackageLoader<
    TrainPackage | TrainTourPackage
> {
    private readonly _adultCount: number;
    private readonly _childCount: number;
    private readonly _infantCount: number;
    private readonly _sellType: SellTypeCode;
    private readonly _exclusive: boolean;
    private _tripType?: TripType;
    private _availableStatus: GetAvailableStatus;
    private _selectedCapacityId?: number;
    constructor({
        originCode,
        destinationCode,
        date,
        adultCount,
        childCount,
        infantCount,
        sellType,
        exclusive,
        url,
        originName,
        destinationName,
        tripType,
        availableStatus = GetAvailableStatus.Both,
        selectedCapacityId,
    }: TrainPackageLoaderConfig) {
        super({
            totalSeatCount: adultCount + childCount,
            destinationCode,
            destinationName,
            originCode,
            originName,
            date,
            url,
        });
        this._adultCount = adultCount;
        this._childCount = childCount;
        this._infantCount = infantCount;
        this._sellType = sellType;
        this._exclusive = exclusive;
        this._tripType = tripType;
        this._availableStatus = availableStatus;
        this._selectedCapacityId = selectedCapacityId;
    }

    get adultCount() {
        return this._adultCount;
    }

    get childCount() {
        return this._childCount;
    }

    get infantCount() {
        return this._infantCount;
    }

    get availableStatus() {
        return this._availableStatus;
    }

    get selectedCapacityId() {
        return this._selectedCapacityId;
    }

    set availableStatus(status: GetAvailableStatus) {
        this._availableStatus = status;
    }

    set selectedCapacityId(capacityId: number | undefined) {
        this._selectedCapacityId = capacityId;
    }

    override compareTo(other: TrainPackageLoaderConfig): boolean {
        if (this._originCode !== other.originCode) {
            return false;
        }
        if (this._destinationCode !== other.destinationCode) {
            return false;
        }
        if (this.date.toString() !== other.date.toString()) {
            return false;
        }
        if (this._adultCount !== other.adultCount) {
            return false;
        }
        if (this._childCount !== other.childCount) {
            return false;
        }
        if (this._infantCount !== other.infantCount) {
            return false;
        }
        if (this._sellType !== other.sellType) {
            return false;
        }
        if (this._exclusive !== other.exclusive) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this._availableStatus !== other.availableStatus) {
            return false;
        }
        return true;
    }

    override async *_getPackages(
        axiosParams?: any
    ): AsyncGenerator<
        TripPackageLoaderResult<TrainPackage | TrainTourPackage>
    > {
        try {
            const paramData: TrainApiApiGetAvailableV2GetRequest = {
                from: parseInt(this._originCode),
                to: parseInt(this._destinationCode),
                date: dayConfig.gregory(this.date).format(formatter()),
                adultCount: this._adultCount,
                childCount: this._childCount,
                infantCount: this._infantCount,
                exclusive: this._exclusive,
                availableStatus: this._availableStatus,
                selectedCapacityId: this._selectedCapacityId,
                genderCode: this._sellType,
            };
            const { data } = await TrainApiApiGetAvailableV2Get(
                paramData,
                axiosParams
            );

            const packages =
                data.trains
                    ?.filter(train =>
                        train.prices?.some(p => p.sellType === this._sellType)
                    )
                    .map(train => {
                        const priceClass = train.prices?.find(
                            p => p.sellType === this._sellType
                        );
                        return (
                            priceClass?.classes?.map(
                                c =>
                                    new TrainPackage(
                                        train,
                                        this._totalSeatCount,
                                        this._sellType,
                                        this._exclusive,
                                        priceClass?.classes?.indexOf(c) ?? 0,
                                        c.minPersons,
                                        c.id,
                                        this._tripType,
                                        c.score ?? undefined
                                    )
                            ) ?? []
                        );
                    })
                    .flat() ?? [];
            const tours =
                data.trainPackages
                    ?.map(pack => pack.trains)
                    .filter(train =>
                        // check for arrival only, with high chance: always true
                        train?.[0].prices?.some(
                            p => p.sellType === this._sellType
                        )
                    )
                    .map(
                        train =>
                            new TrainTourPackage(
                                train![0],
                                train![1],
                                this._totalSeatCount,
                                this._sellType,
                                this._exclusive
                            )
                    ) ?? [];
            yield {
                list: [...packages, ...tours],
                originLocation: {
                    latitude: data.fromLocation?.latitude ?? 0,
                    longitude: data.fromLocation?.longitude ?? 0,
                },
                destinationLocation: {
                    latitude: data.toLocation?.latitude ?? 0,
                    longitude: data.toLocation?.longitude ?? 0,
                },
                filterMetaData: data.filters ?? {},
                metaData: {
                    hasPreSell: data.meta?.alwaysHasTrain,
                    routeNotFound: !data.meta?.alwaysHasTrain,
                },
            };
        } catch (error) {
            const e = error as AxiosError;
            const data = e.response?.data as any;
            if (data?.ErrorNo?.toString() === '100') {
                const toDate = dayConfig
                    .gregory()
                    .add(data?.DisconnectedUntil, 'seconds')
                    .toDate();
                throw new ServerDisconnectToDateException(
                    toDate,
                    'متاسفیم! در حال حاضر امکان خرید بلیط قطار به علت عملیات پشتیبانی راه آهن وجود ندارد'
                );
            }
            if (
                data?.ErrorNo === '2' ||
                data?.ErrorType === 'GetAvailableError' ||
                data?.Error === 'هیچ‌کدام از سرویس‌ها پاسخ ندادند'
            ) {
                throw new TrainNoServerResponseError();
            }
            throw error;
        }
    }
}
